import { destroy, stopSubmit } from 'redux-form'
import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import {
  IRequestCreateRoom,
  IRoomWasCreated,
  roomWasCreated,
} from '../../actions/bookingCalendar/room/requestCreateRoom'
import { toggleRoomModalIsSaving } from '../../actions/bookingCalendar/roomModal/toggleRoomModal'
import { callFetch } from '../../actions/fetch'
import closeModal from '../../actions/Modals/closeModal'
import fireToastError from '../../actions/toasts/fireToastError'
import { Modals } from '../../containers/Modals/ModalContainer'
import { database } from '../../database'
import i18n from '../../i18n'
import { createRoom as requestCreate } from '../../models/Room'
import { RoomActions } from '../../reducers/bookingCalendar/actionNames'
import { getCalendarId } from '../../reducers/bookingCalendar/selectors/calendar'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import { Mixpanel } from '../../utils/Mixpanel'
import { getUserId } from '../../reducers/userAccount/selectors/userAccount'

function* createRoom(action: IRequestCreateRoom) {
  yield put(toggleRoomModalIsSaving(true))

  try {
    const actionNames = fetchActionsName(ActionNames.CreateRoom)
    const [{ calendarId, service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, requestCreate)),
    ])

    const rooms = yield call(service, calendarId, action.room)
    const userId = yield select(getUserId)

    for (const room of rooms) {
      Mixpanel.people.increment(userId, 'Rooms Added', 1)
      Mixpanel.track('Add New Room Completed', {
        'Room ID': Number(room.id),
        'Room Name': room.name,
        'Room Name Length': room.name.length,
        'Room Type': mapRoomTypeToEventString(room.type),
        'Room No. Of People': Number(room.roomSize),
        'Room Extra Beds': Number(room.additionalBeds),
        'Room Single Beds': Number(room.singleBed),
        'Room Double Beds': Number(room.doubleBed),
        'Room Display Order': Number(room.position),
        'Room Custom Color': !!room.customColor,
        'Room Description Length': room.description
          ? Number(room.description.length)
          : undefined,
        'Room Additional equipment Length': room.additionalEquipment
          ? Number(room.additionalEquipment.length)
          : undefined,
        'Room Size': room.size ? Number(room.size) : undefined,
      })

      yield put(roomWasCreated(room))
    }

    yield destroy(action.formName)
    yield put(closeModal(Modals.AddRoom))
  } catch (e) {
    yield stopSubmit(action.formName, e)
    yield put(fireToastError(i18n.t('error_server_response_3')))
  } finally {
    yield put(toggleRoomModalIsSaving(false))
  }
}

export const mapRoomTypeToEventString = (roomType: string) => {
  const roomTypeNum = Number(roomType)

  switch (roomTypeNum) {
    case 1:
      return 'Room'
    case 2:
      return 'Apartment'
    case 3:
      return 'Maisonette'
    case 4:
      return 'Studio'
    case 5:
      return 'Bungalow'
    case 6:
      return 'Camper'
    case 7:
      return 'Villa'
    case 8:
      return 'Tent'
    case 9:
      return 'Lodge'
    case 10:
      return 'Bed'
    case 11:
      return 'Boat'
    case 12:
      return 'House'
    default:
      return 'Room'
  }
}

function* putRoomIntoDb(action: IRoomWasCreated) {
  const calendarId = yield select(getCalendarId)
  yield database.rooms.add({
    ...action.room,
    calendarId,
  })
}

export default function*() {
  yield takeLatest(RoomActions.RequestCreateRoom, createRoom)
  yield takeEvery(RoomActions.RoomWasCreated, putRoomIntoDb)
}
